import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, InputAdornment, Box, Typography,
  Divider, Paper, Avatar, Select, MenuItem, FormControl, InputLabel,
  FormHelperText, Autocomplete, Chip
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Close, PersonAdd, 
  Business, Email, Lock, Phone, Language, Campaign, Casino
} from '@mui/icons-material';
import { parsePhoneNumber, isValidPhoneNumber, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { checkemailexist } from '../pages/UserManager/Vendor/Helper';
import { getCompanyLogos } from '../pages/UserManager/Client/Helper';

const SignupForm = ({ open, onClose, onSubmit, title = "Add Client", submitButtonText = "Sign Up", initialData = {}, isLoading = false}) => {
  const [formData, setFormData] = useState(() => ({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    companyName: initialData.companyName || '',
    email: initialData.email || '',
    password: initialData.password || '',
    countryCode: initialData.countryCode || '91',
    phone: initialData.phone || '',
    website: initialData.website || '',
    utmSource: '',
    ...initialData
  }));

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [companyLogos, setCompanyLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const countries = getCountries();
  const countryOptions = countries.map(country => ({
    code: getCountryCallingCode(country), // returns '91'
    name: new Intl.DisplayNames(['en'], { type: 'region' }).of(country),
  }));

  useEffect(() => {
    if (open) {
      setFormData({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        companyName: initialData.companyName || '',
        email: initialData.email || '',
        password: initialData.password || '',
        countryCode: initialData.countryCode || '91',
        phone: initialData.phone || '',
        website: initialData.website || '',
        utmSource: initialData.utmSource || '',
        ...initialData
      });
      setErrors({});
      setShowPassword(false);
    }
  }, [open]);

  const generateRandomPassword = () => {
    const length = 8;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const simpleSymbols = '@#$!';
    
    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += simpleSymbols[Math.floor(Math.random() * simpleSymbols.length)];
    const easyChars = lowercase + uppercase + numbers;
    for (let i = password.length; i < length; i++) {
      password += easyChars[Math.floor(Math.random() * easyChars.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };
  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    handleInputChange('password', newPassword);
    setShowPassword(true);
  };

  const isDisposableEmail = (email) => {
    const disposableEmailDomains = [
      'yopmail.com',
      'yopmail.fr',
      'yopmail.net',
      '10minutemail.com',
      'tempmail.com',
      'mailinator.com',
      'guerrillamail.com',
      'throwaway.email',
      'temp-mail.org',
      'getnada.com',
      'mohmal.com',
      'fakeinbox.com',
      'trashmail.com',
      'mintemail.com',
      'sharklasers.com',
      'getairmail.com',
      'meltmail.com',
      'emailondeck.com',
      'maildrop.cc',
      'mailsac.com',
      'tempail.com',
      'mailcatch.com',
      'dispostable.com',
      'mailnesia.com',
      'mytemp.email',
      'tempinbox.co.uk',
      'mytrashmail.com',
      'spamgourmet.com',
      'mailmoat.com',
      'emailias.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return disposableEmailDomains.some(disposableDomain => 
      domain === disposableDomain || domain?.endsWith(`.${disposableDomain}`)
    );
  };

  const isValidEmailProvider = (email) => {
    const allowedPublicProviders = [
      'gmail.com',
      'googlemail.com',
      'yahoo.com',
      'yahoo.co.uk',
      'yahoo.co.in',
      'ymail.com',
      'rocketmail.com'
    ];
    
    const publicEmailProviders = [
      'outlook.com',
      'hotmail.com',
      'live.com',
      'msn.com',
      'aol.com',
      'icloud.com',
      'me.com',
      'mac.com',
      'protonmail.com',
      'proton.me',
      'zoho.com',
      'mail.com',
      'gmx.com',
      'rediffmail.com',
      'inbox.com',
      'fastmail.com',
      'yandex.com',
      'mail.ru',
      'qq.com',
      '163.com',
      'sina.com',
      'naver.com',
      'daum.net',
      'hanmail.net'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    if (allowedPublicProviders.some(provider => 
      domain === provider || domain?.endsWith(`.${provider}`)
    )) {
      return true;
    }
    if (publicEmailProviders.some(provider => 
      domain === provider || domain?.endsWith(`.${provider}`)
    )) {
      return false;
    }
    return true;
  };
  
  useEffect(() => {
    const fetchCompanyLogos = async () => {
      setLoadingLogos(true);
      try {
        const response = await getCompanyLogos();
        setCompanyLogos(response.data.result.rows || []);
      } catch (error) {
        console.error('Error fetching company logos:', error);
      } finally {
        setLoadingLogos(false);
      }
    };

    if (open) {
      fetchCompanyLogos();
    }
  }, [open]);
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    if (formData.lastName.trim() && formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }
    if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
    } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const hasSpaces = /\s/.test(formData.email);
    
        if (hasSpaces) {
        newErrors.email = 'Email cannot contain spaces';
        } else if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        } else if (isDisposableEmail(formData.email)) {
        newErrors.email = 'Disposable email addresses (like yopmail) are not allowed. Please use a valid email address.';
        } else if (!isValidEmailProvider(formData.email)) {
        newErrors.email = 'Only company emails, Gmail, and Yahoo are allowed. Please use a valid email address.';
        }
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    // if (!formData.phone.trim()) {
    //   newErrors.phone = 'Phone number is required';
    // } else {
    //   try {
    //     const fullPhoneNumber = `${formData.countryCode}${formData.phone}`;
    //     if (!isValidPhoneNumber(fullPhoneNumber)) {
    //       newErrors.phone = 'Please enter a valid phone number';
    //     }
    //   } catch (error) {
    //     newErrors.phone = 'Please enter a valid phone number';
    //   }
    // }
    if (formData.website && formData.website.trim()) {
      const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlRegex.test(formData.website)) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleCompanySelect = (event, newValue) => {
    setSelectedCompany(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        utmSource: newValue.name.toLowerCase().replace(/\s+/g, ''),
        branding_logo: newValue.logo_url
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        utmSource: '',
        branding_logo: ''
      }));
    }
  };
  
  const handleInputChange = (field, value) => {
    let cleanValue = value; 
    if (field === 'email') {
      cleanValue = value.toLowerCase().replace(/\s+/g, '');
    } else if (field === 'utmSource') {
      cleanValue = value.toLowerCase().replace(/\s+/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [field]: cleanValue
    }));
  
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) return;
  
    try {
      const checkRes = await checkemailexist({ email: formData.email });
      if (checkRes?.data?.msg === 'Email already exists') {
        setErrors(prev => ({
          ...prev,
          email: 'Email already exists. Please use another.'
        }));
        return;
      }
      const fullPhoneNumber = `${formData.countryCode}${formData.phone}`;
      const submissionData = {
        ...formData,
        fullPhoneNumber
      };
      if (formData.branding_logo) {
        submissionData.branding_logo = formData.branding_logo;
      }

      onSubmit(submissionData);
  
    } catch (err) {
      console.error('Email check failed:', err);
      setErrors(prev => ({
        ...prev,
        email: 'Failed to verify email. Please try again.'
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
      countryCode: 'IN',
      phone: '',
      website: '',
      utmSource: '',
      branding_logo: ''
    });
    setErrors({});
    setShowPassword(false);
    setSelectedCompany(null);
    onClose();
  };

  const selectedCountry = countryOptions.find(c => c.code === formData.countryCode);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth 
      PaperProps={{ 
        style: { 
          maxHeight: '95vh',
          borderRadius: 20, 
          padding: 0,
          overflow: 'visible',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)'
        } 
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2EBAA3 0%, #26a691 100%)',
          color: 'white',
          px: 3,
          py: 2,
          position: 'relative',
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  width: 40,
                  height: 40,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <PersonAdd sx={{ color: 'white', fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0 }}>
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Fill in the details to create a new account
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={handleClose} 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers={false} sx={{ px: 3, paddingTop: 3, paddingBottom: 1, overflowY: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAdd sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {/* <PersonAdd sx={{ color: 'text.secondary', fontSize: 20 }} /> */}
                      </InputAdornment>
                    ),
                  }}
              />
            </Box>
            <TextField
              label="Company Name"
              fullWidth
              required
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              fullWidth
              required
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Password *
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGeneratePassword}
                  sx={{ 
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: '#2EBAA3',
                    borderColor: '#2EBAA3',
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    height: 28,
                    borderRadius: 2,
                    '&:hover': { 
                      bgcolor: 'rgba(46, 186, 163, 0.1)',
                      borderColor: '#26a691',
                      color: '#26a691'
                    }
                  }}
                  startIcon={<Casino sx={{ fontSize: '14px !important' }} />}
                >
                  Generate Password
                </Button>
              </Box>
            <TextField
              fullWidth
              required
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              placeholder="Enter your password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl 
                sx={{ minWidth: 200 }} 
                error={!!errors.countryCode}
              >
                <InputLabel>Country Code *</InputLabel>
                <Select
                  value={formData.countryCode}
                  label="Country Code *"
                  onChange={(e) => handleInputChange('countryCode', e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 240, // Limit dropdown height
                        overflowY: 'auto', // Enable scrolling
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {countryOptions.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} (+{country.code})
                    </MenuItem>                  
                  ))}
                </Select>
                {errors.countryCode && (
                  <FormHelperText>{errors.countryCode}</FormHelperText>
                )}
              </FormControl>

              <TextField
                label="Phone No."
                fullWidth
                required
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder="Enter phone number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Website"
              fullWidth
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              error={!!errors.website}
              helperText={errors.website}
              placeholder="https://example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Language sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
              <Autocomplete
              fullWidth
              options={companyLogos}
              getOptionLabel={(option) => option.name || ''}
              value={selectedCompany}
              onChange={handleCompanySelect}
              loading={loadingLogos}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="UTM Source"
                  placeholder="Select a company"
                  error={!!errors.utmSource}
                  helperText={errors.utmSource}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Campaign sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography>{option.name}</Typography>
                </Box>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    avatar={<Avatar src={option.logo_url} />}
                    label={option.name}
                  />
                ))
              }
            />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}
            sx={{ backgroundColor: '#2EBAA3', color: '#fff', '&:hover': { backgroundColor: '#26a691' } }}>
            {isLoading ? 'Creating...' : submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SignupForm;